import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ImagePlus, Mic } from "lucide-react";

import type { ConversationLiveState } from "@/api/db/messaging-read";
import type { DraftContext } from "@/types/draft";
import { orpc } from "@/client";
import { SendResultView } from "@/components/draft/send-result-view";
import { ComposerAttachments } from "./composer-attachments";
import { ComposerText } from "./composer-text";
import { SendButton } from "@/components/ui/send-button";
import { Button } from "@/components/ui/button";
import { useConversationSession } from "@/hooks/use-conversation-session";
import { useConversationSend } from "@/hooks/use-conversation-send";
import { useAudioRecording } from "@/hooks/use-audio-recording";

import { ComposerReactivation } from "./composer-reactivation";
import { ComposerAudio } from "./composer-audio";
import { ComposerCopilot } from "./composer-copilot";

export function CockpitComposer({
	context,
	state,
}: {
	context: DraftContext;
	state: ConversationLiveState | undefined;
}) {
	const { data: config } = useQuery(orpc.send.config.queryOptions());
	const { session, draft } = useConversationSession(context.person.id);
	const { send } = useConversationSend(context.person.id, state);
	const recording = useAudioRecording(session);
	const input = useRef<HTMLInputElement>(null);
	const [attachmentError, setAttachmentError] = useState<string | null>(null);
	const audioMode = !!draft.audio || recording.phase !== null;
	const windowClosed = state !== undefined && !state.window_open;
	const unavailable = !context.person.sendpulse_id || !state?.window_open;
	const sendDisabled = unavailable || draft.sending;
	const attachmentsDisabled = audioMode || draft.sending || !config?.media.images;

	useEffect(() => {
		if (windowClosed && recording.phase === "recording") {
			recording.stop();
		}
	}, [windowClosed, recording.phase]);

	const addImages = (files: File[]) => {
		if (attachmentsDisabled) {
			return;
		}

		if (
			files.some(
				(file) =>
					!["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
					file.size > 5 * 1024 * 1024,
			)
		) {
			setAttachmentError("Use imagens JPG, PNG ou WebP de até 5 MB.");

			return;
		}

		setAttachmentError(null);
		session.addImages(files);
	};

	return (
		<div
			data-component="cockpit-composer"
			className="shrink-0 border-t border-border bg-card p-3"
			onDragOver={(event) => {
				if (!audioMode && event.dataTransfer.types.includes("Files")) {
					event.preventDefault();
				}
			}}
			onDrop={(event) => {
				event.preventDefault();
				addImages(Array.from(event.dataTransfer.files));
			}}
			onPaste={(event) => {
				if (event.clipboardData.files.length > 0) {
					event.preventDefault();
					addImages(Array.from(event.clipboardData.files));
				}
			}}
		>
			{!context.person.sendpulse_id && (
				<p className="mb-2 text-xs text-muted-foreground">Esta conversa está sem canal de envio.</p>
			)}
			{windowClosed && (
				<div className="mb-2 flex items-center justify-between gap-2">
					<p className="text-xs text-muted-foreground">Janela de resposta encerrada.</p>
					{!!context.person.sendpulse_id && (
						<ComposerReactivation personId={context.person.id} real={!!config?.enabled} />
					)}
				</div>
			)}
			<div className="overflow-hidden workspace-frame focus-within:border-primary/40">
				{audioMode && (
					<ComposerAudio
						audio={draft.audio}
						recording={recording}
						sending={draft.sending}
						disabled={unavailable || !config?.media.audio}
						onSend={() => send("audio")}
					/>
				)}
				{!audioMode && (
					<>
						<ComposerAttachments personId={context.person.id} />
						<ComposerText
							personId={context.person.id}
							sendDisabled={sendDisabled}
							onSend={() => send("text")}
						/>
						<div className="flex items-center justify-between gap-2 px-2 pb-2">
							<div className="flex items-center gap-1">
								<input
									ref={input}
									type="file"
									accept="image/jpeg,image/png,image/webp"
									multiple
									className="hidden"
									onChange={(event) => {
										addImages(Array.from(event.target.files || []));
										event.target.value = "";
									}}
								/>
								<Button
									size="icon"
									variant="ghost"
									aria-label="Anexar imagens"
									disabled={attachmentsDisabled}
									onClick={() => input.current?.click()}
								>
									<ImagePlus />
								</Button>
								<Button
									size="icon"
									variant="ghost"
									aria-label="Gravar áudio"
									disabled={sendDisabled || !config?.media.audio}
									onClick={recording.start}
								>
									<Mic />
								</Button>
							</div>
							<SendButton
								real={!!config?.enabled}
								disabled={
									sendDisabled ||
									(!draft.text.trim() && draft.images.length === 0) ||
									(draft.images.length > 0 && draft.text.length > 1024)
								}
								pending={draft.sending}
								label={draft.sending ? "Enviando..." : config?.enabled ? "Enviar" : "Simular envio"}
								onClick={() => send("text")}
							/>
						</div>
						<ComposerCopilot personId={context.person.id} windowClosed={windowClosed} />
					</>
				)}
			</div>
			{recording.error && (
				<p role="alert" className="mt-2 text-xs text-destructive">
					Não foi possível gravar. Confira a permissão do microfone.
				</p>
			)}
			{attachmentError && (
				<p role="alert" className="mt-2 text-xs text-destructive">
					{attachmentError}
				</p>
			)}
			{draft.sendError && (
				<p role="alert" className="mt-2 text-xs text-destructive">
					{draft.sendError}
				</p>
			)}
			{draft.sendResult && (
				<div className="mt-2">
					<SendResultView result={draft.sendResult} />
				</div>
			)}
		</div>
	);
}
